/**
 * WF-Progress - Composable progress bar component for wizard forms
 *
 * A standalone progress indicator that can be placed inside or outside the wizard.
 * Connects to wizard-form via `for` attribute or auto-discovers parent wizard.
 *
 * @example
 * <!-- Inside wizard (auto-connects) -->
 * <wizard-form id="my-form">
 *   <wf-progress></wf-progress>
 *   <wf-step>...</wf-step>
 * </wizard-form>
 *
 * @example
 * <!-- Outside wizard (uses for attribute) -->
 * <wf-progress for="my-form"></wf-progress>
 * <wizard-form id="my-form">...</wizard-form>
 */

import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { wfProgressStyles } from './wf-progress.styles.js';
import type { WizardForm, StepChangeDetail } from './wizard-form.js';

@customElement('wf-progress')
export class WfProgress extends LitElement {
  static styles = wfProgressStyles;

  /**
   * ID of the wizard-form to connect to.
   * If not specified, auto-connects to parent wizard-form.
   */
  @property({ type: String })
  for = '';

  @state()
  private _currentStep = 1;

  @state()
  private _totalSteps = 1;

  @state()
  private _isComplete = false;

  private _wizard: WizardForm | null = null;
  private _boundHandleStepChange = this._handleStepChange.bind(this);
  private _boundHandleSuccess = this._handleSuccess.bind(this);

  private _boundHandleStepsDiscovered = this._handleStepsDiscovered.bind(this);

  connectedCallback(): void {
    super.connectedCallback();

    // Listen for steps-discovered event IMMEDIATELY on document (bubbles up)
    // This catches the event even if wizard-form fires it before we connect to it
    document.addEventListener('wf:steps-discovered', this._boundHandleStepsDiscovered as EventListener);

    // Defer wizard connection to allow DOM to settle
    requestAnimationFrame(() => {
      this._connectToWizard();
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('wf:steps-discovered', this._boundHandleStepsDiscovered as EventListener);
    this._disconnectFromWizard();
  }

  private _connectToWizard(): void {
    // Find wizard by ID or by closest parent
    if (this.for) {
      this._wizard = document.getElementById(this.for) as WizardForm | null;
    } else {
      this._wizard = this.closest('wizard-form') as WizardForm | null;
    }

    if (this._wizard) {
      // Get initial state
      this._currentStep = this._wizard.currentStep || 1;
      this._totalSteps = this._wizard.totalSteps || 1;
      this._isComplete = this._wizard.isSubmitted || false;

      // Listen for step changes
      this._wizard.addEventListener('wf:step-change', this._boundHandleStepChange as EventListener);

      // Listen for success (form complete)
      this._wizard.addEventListener('wf:success', this._boundHandleSuccess as EventListener);

      // Note: wf:steps-discovered is listened on document in connectedCallback
    }
  }

  private _disconnectFromWizard(): void {
    if (this._wizard) {
      this._wizard.removeEventListener('wf:step-change', this._boundHandleStepChange as EventListener);
      this._wizard.removeEventListener('wf:success', this._boundHandleSuccess as EventListener);
      this._wizard = null;
    }
  }

  private _handleStepChange(e: CustomEvent<StepChangeDetail>): void {
    this._currentStep = e.detail.to;
    // Re-read total steps in case it changed
    if (this._wizard) {
      this._totalSteps = this._wizard.totalSteps;
    }
  }

  private _handleStepsDiscovered(e: CustomEvent): void {
    const { wizard, wizardId, totalSteps, currentStep } = e.detail;

    // Check if this event is from our target wizard
    if (this.for) {
      // We have a specific target - check if event is from that wizard
      if (wizardId !== this.for) {
        return;
      }
    } else {
      // No specific target - check if event is from a parent wizard
      const parentWizard = this.closest('wizard-form');
      if (!parentWizard || parentWizard !== wizard) {
        return;
      }
    }

    // Connect to wizard if not already connected
    if (!this._wizard && wizard) {
      this._wizard = wizard;
      wizard.addEventListener('wf:step-change', this._boundHandleStepChange as EventListener);
      wizard.addEventListener('wf:success', this._boundHandleSuccess as EventListener);
    }

    // Update state from event detail
    this._totalSteps = totalSteps;
    this._currentStep = currentStep;
  }

  private _handleSuccess(): void {
    this._isComplete = true;
  }

  render() {
    // Hide progress when form is complete
    if (this._isComplete) {
      return null;
    }

    const segments = [];
    for (let i = 1; i <= this._totalSteps; i++) {
      const completed = i < this._currentStep;
      const active = i === this._currentStep;
      segments.push(html`
        <div
          class="wf-progress-segment ${completed ? 'completed' : ''} ${active ? 'active' : ''}"
          data-testid="wf-progress-segment"
          data-step="${i}"
          data-completed="${completed}"
          data-active="${active}"
          role="progressbar"
          aria-valuenow="${this._currentStep}"
          aria-valuemin="1"
          aria-valuemax="${this._totalSteps}"
        ></div>
      `);
    }
    return html`<div class="wf-progress" data-testid="wf-progress-bar" role="group" aria-label="Form progress">${segments}</div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-progress': WfProgress;
  }
}
